import { useState, useCallback, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { 
  Settings,
  Trash, 
  CheckSquare,
  Loader2,
  Info
} from "lucide-react";
import { useCategories } from "@/hooks/useCategories";
import { usePlans } from "@/hooks/usePlans";
import { useTags, useAssignTags } from "@/hooks/useTags";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDeleteChannels, useBulkUpdateChannels } from '@/hooks/useChannels';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { channelBulkUpdateSchema } from "../types/form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { getPlanColor } from "@/utils/colors";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { useChannelStore } from '@/stores/channelStore';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

interface ChannelBulkActionsProps {
  providerId: number;
  versionId: number;
  selectedIds: number[];
}

// Extended form data type to include category and tag operations
interface BulkUpdateFormData {
  channelIds: number[];
  category_id?: number | null;
  category_ids?: number[];
  default_category_id?: number | null;
  categoryUpdateMode?: 'add' | 'replace' | 'remove';
  plan_ids?: number[];
  tag_ids?: number[];
  tagUpdateMode?: 'add' | 'replace' | 'remove';
  description?: string;
  price?: number | null;
  is_local?: boolean;
}

export function ChannelBulkActions({
  providerId,
  versionId,
  selectedIds
}: ChannelBulkActionsProps) {
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [updatingCategory, setUpdatingCategory] = useState(false);
  const [updatingPlans, setUpdatingPlans] = useState(false);
  const [updatingDescription, setUpdatingDescription] = useState(false);
  const [updatingPrice, setUpdatingPrice] = useState(false);
  const [updatingLocalStatus, setUpdatingLocalStatus] = useState(false);
  const [updatingTags, setUpdatingTags] = useState(false);
  const [categoryUpdateMode, setCategoryUpdateMode] = useState<'add' | 'replace' | 'remove'>('add');
  const [tagUpdateMode, setTagUpdateMode] = useState<'add' | 'replace' | 'remove'>('add');
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [defaultCategoryId, setDefaultCategoryId] = useState<number | null>(null);
  const [isLocalChannel, setIsLocalChannel] = useState(false);
  
  // Get region context from URL params and channel store
  const { regionId } = useParams<{ regionId?: string }>();
  const { currentRegionId } = useChannelStore();
  // Use either the URL param or the store context
  const hasRegionContext = !!(regionId || currentRegionId);
  
  const { data: categories } = useCategories(providerId, versionId);
  const { data: plans } = usePlans(providerId, versionId);
  const { data: tags } = useTags(providerId, versionId, regionId ? Number(regionId) : currentRegionId || 0);
  const assignTagsMutation = useAssignTags();
  const { mutate: deleteChannels, isPending: isDeleting } = useDeleteChannels();
  const bulkUpdateMutation = useBulkUpdateChannels();
  const queryClient = useQueryClient();
  
  // Setup form with zod validation
  const form = useForm<BulkUpdateFormData>({
    resolver: zodResolver(channelBulkUpdateSchema),
    defaultValues: {
      channelIds: selectedIds,
      category_id: null,
      plan_ids: [],
      description: '',
      price: null
    }
  });

  // Create a filtered list of categories for default selection
  const availableDefaultCategories = useMemo(() => {
    // Use all categories instead of just the selected ones
    return categories || [];
  }, [categories]);

  // Reset form when dialog closes
  const handleDialogClose = () => {
    setIsBulkUpdateOpen(false);
    setUpdatingCategory(false);
    setUpdatingPlans(false);
    setUpdatingDescription(false);
    setUpdatingPrice(false);
    setUpdatingLocalStatus(false);
    setUpdatingTags(false);
    setCategoryUpdateMode('add');
    setTagUpdateMode('add');
    setSelectedCategories([]);
    setSelectedTags([]);
    setDefaultCategoryId(null);
    setIsLocalChannel(false);
    form.reset({
      channelIds: selectedIds,
      category_id: null,
      plan_ids: [],
      description: '',
      price: null,
      is_local: false
    });
  };

  // Handle form submission
  const onSubmit = useCallback(async (values: BulkUpdateFormData) => {
    try {
      // Process category updates
      if (updatingCategory) {
        // Extend the form values with category operations
        values.categoryUpdateMode = categoryUpdateMode;
        values.category_ids = selectedCategories;
        values.default_category_id = defaultCategoryId;
        
        console.log('[BulkActions] Category update data:', {
          mode: categoryUpdateMode,
          selectedCategories,
          defaultCategoryId,
          selectedCount: selectedCategories.length
        });
      }

      // Process tag updates
      if (updatingTags && hasRegionContext) {
        values.tagUpdateMode = tagUpdateMode;
        values.tag_ids = selectedTags;
        
        console.log('[BulkActions] Tag update data:', {
          mode: tagUpdateMode,
          selectedTags,
          selectedCount: selectedTags.length
        });
      }

      // Only include fields that are being updated
      const updateData: BulkUpdateFormData = {
        channelIds: values.channelIds
      };

      if (updatingCategory) {
        updateData.category_ids = values.category_ids;
        updateData.default_category_id = values.default_category_id;
        updateData.categoryUpdateMode = values.categoryUpdateMode;
      }

      if (updatingPlans) {
        updateData.plan_ids = values.plan_ids;
      } else {
        // Explicitly set plan_ids to undefined when not updating plans
        // This ensures that the backend knows we're not trying to update plans
        updateData.plan_ids = undefined;
      }

      if (updatingDescription) {
        updateData.description = values.description;
      }

      if (updatingPrice) {
        updateData.price = values.price;
      }
      
      if (updatingLocalStatus) {
        updateData.is_local = isLocalChannel;
      }

      console.log('[BulkActions] Submitting update data:', updateData);

      // Format data for channel updates - create one update object per channel
      const updateInputs = selectedIds.map(id => ({
        id,
        providerId,
        versionId,
        categoryIds: updateData.category_ids,
        defaultCategoryId: updateData.default_category_id,
        categoryUpdateMode: updateData.categoryUpdateMode, 
        planIds: updatingPlans ? (updateData.plan_ids || []) : undefined,
        description: updateData.description,
        price: updateData.price,
        is_local: updatingLocalStatus ? isLocalChannel : undefined
      }));

      // Execute channel property updates
      if (updatingCategory || updatingPlans || updatingDescription || updatingPrice || updatingLocalStatus) {
        bulkUpdateMutation.mutate({
          updates: updateInputs,
          providerId,
          versionId
        }, {
          onSuccess: () => {
            // Invalidate queries with region context
            const regionIdParam = regionId ? Number(regionId) : currentRegionId;
            queryClient.invalidateQueries({ queryKey: ['channels', providerId, versionId, regionIdParam] });
          },
          onError: (error) => {
            console.error('Bulk update error:', error);
            toast.error(error instanceof Error ? error.message : "Failed to update channels");
          }
        });
      }

      // Execute tag assignments separately
      if (updatingTags && hasRegionContext && selectedTags.length > 0) {
        assignTagsMutation.mutate({
          channelIds: selectedIds,
          tagIds: selectedTags,
          operation: tagUpdateMode
        });
      } else if (updatingTags && hasRegionContext && tagUpdateMode === 'remove') {
        // Handle remove operation even with no tags selected (remove all tags)
        assignTagsMutation.mutate({
          channelIds: selectedIds,
          tagIds: [], // Empty array for remove all
          operation: 'remove'
        });
      }
      
      // Close the dialog after submitting
      handleDialogClose();
    } catch (error) {
      console.error('Error preparing bulk update:', error);
      toast.error("Failed to prepare update data");
    }
  }, [
    selectedIds, 
    providerId, 
    versionId, 
    updatingCategory,
    updatingPlans,
    updatingDescription,
    updatingPrice,
    updatingLocalStatus,
    updatingTags,
    categoryUpdateMode,
    tagUpdateMode,
    selectedCategories,
    selectedTags,
    defaultCategoryId,
    isLocalChannel,
    hasRegionContext,
    bulkUpdateMutation,
    assignTagsMutation,
    handleDialogClose,
    queryClient,
    regionId,
    currentRegionId
  ]);

  return (
    <div className="p-4 border rounded-md bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CheckSquare className="h-5 w-5 text-primary" />
          <span>
            <span className="font-semibold">{selectedIds.length}</span> channels selected
          </span>
        </div>
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => setIsBulkUpdateOpen(true)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Edit Selected
          </Button>
          
          <Button
            variant="destructive"
            onClick={() => {
              if (confirm(`Are you sure you want to delete ${selectedIds.length} channels?`)) {
                deleteChannels({ 
                  ids: selectedIds, 
                  providerId, 
                  versionId 
                });
              }
            }}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash className="h-4 w-4 mr-2" />
            )}
            Delete
          </Button>
        </div>
      </div>

      {/* Bulk Edit Dialog */}
      <Dialog 
        open={isBulkUpdateOpen} 
        onOpenChange={(open) => {
          if (!open) handleDialogClose();
          else setIsBulkUpdateOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {selectedIds.length} Channels</DialogTitle>
            <DialogDescription>
              Select which properties you want to update.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Category Updates */}
              <div className="space-y-4 pb-4 border-b">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="updateCategory" 
                    checked={updatingCategory}
                    onCheckedChange={(checked) => setUpdatingCategory(!!checked)}
                  />
                  <Label htmlFor="updateCategory">Update Categories</Label>
                </div>
                
                {updatingCategory && (
                  <div className="pl-6 space-y-4">
                    <RadioGroup
                      value={categoryUpdateMode}
                      onValueChange={(value) => {
                        const newMode = value as 'add' | 'replace' | 'remove';
                        setCategoryUpdateMode(newMode);
                        
                        // If switching to 'replace' mode and no categories are selected,
                        // show a warning that this will remove all categories
                        if (newMode === 'replace' && selectedCategories.length === 0) {
                          console.log('[BulkActions] Warning: Replace mode with no categories will remove all categories');
                        }
                        
                        // If switching to remove mode, we don't need a default category
                        if (newMode === 'remove') {
                          // No default category needed for remove mode
                          setDefaultCategoryId(null);
                        } else if (newMode === 'add' || newMode === 'replace') {
                          // For add/replace modes, keep existing default if valid,
                          // otherwise set first selected category as default if available
                          const isCurrentDefaultValid = defaultCategoryId && selectedCategories.includes(defaultCategoryId);
                          if (!isCurrentDefaultValid && selectedCategories.length > 0) {
                            setDefaultCategoryId(selectedCategories[0]);
                          }
                        }
                      }}
                      className="space-y-4"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="add" id="option-add" />
                          <Label htmlFor="option-add">Add to existing categories</Label>
                          <Collapsible>
                            <CollapsibleTrigger className="ml-2 text-muted-foreground hover:text-foreground">
                              <Info size={16} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="ml-6 mt-1 text-xs text-muted-foreground border-l-2 pl-2">
                              Preserves all existing categories that channels already have.
                              Your selected categories will be added to the channels in addition to
                              their current categories.
                              <div className="mt-1 font-medium">Example:</div>
                              If a channel has categories A and B, and you select C and D, 
                              after the update the channel will have A, B, C, and D.
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="replace" id="option-replace" />
                          <Label htmlFor="option-replace">
                            Replace all categories
                            {selectedCategories.length === 0 && (
                              <span className="text-xs text-destructive ml-2">(will remove all categories)</span>
                            )}
                          </Label>
                          <Collapsible>
                            <CollapsibleTrigger className="ml-2 text-muted-foreground hover:text-foreground">
                              <Info size={16} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="ml-6 mt-1 text-xs text-muted-foreground border-l-2 pl-2">
                              Completely removes all existing categories from selected channels and replaces them with your selection.
                              If you don't select any categories, all categories will be removed.
                              <div className="mt-1 font-medium">Examples:</div>
                              <ul className="list-disc list-inside">
                                <li>If a channel has categories A and B, and you select C, 
                                    after the update the channel will only have C.</li>
                                <li>If a channel has categories A and B, and you select nothing, 
                                    after the update the channel will have no categories.</li>
                              </ul>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="remove" id="option-remove" />
                          <Label htmlFor="option-remove">Remove selected categories</Label>
                          <Collapsible>
                            <CollapsibleTrigger className="ml-2 text-muted-foreground hover:text-foreground">
                              <Info size={16} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="ml-6 mt-1 text-xs text-muted-foreground border-l-2 pl-2">
                              Only removes the specific categories you select from channels that already have them assigned.
                              Categories that don't exist on a channel will be ignored.
                              <div className="mt-1 font-medium">Example:</div>
                              If a channel has categories A, B, and C, and you select B, 
                              after the update the channel will have A and C.
                              <div className="mt-1 text-orange-500">Note: If you select a category that isn't assigned to 
                              any channels, nothing will happen to those channels.</div>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      </div>
                    </RadioGroup>

                    <div className="space-y-2">
                      <Label>Select Categories</Label>
                      <ScrollArea className="h-[120px] border rounded-md p-2">
                        <div className="space-y-2">
                          {categories?.map(category => (
                            <div key={category.id} className="flex items-center space-x-2">
                              <Checkbox 
                                id={`cat-${category.id}`} 
                                checked={selectedCategories.includes(category.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedCategories(prev => [...prev, category.id]);
                                    if (!defaultCategoryId) {
                                      setDefaultCategoryId(category.id);
                                    }
                                  } else {
                                    setSelectedCategories(prev => prev.filter(id => id !== category.id));
                                    if (defaultCategoryId === category.id) {
                                      const remainingSelected = selectedCategories.filter(id => id !== category.id);
                                      setDefaultCategoryId(remainingSelected.length > 0 ? remainingSelected[0] : null);
                                    }
                                  }
                                }}
                              />
                              <Label htmlFor={`cat-${category.id}`}>{category.name}</Label>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    {selectedCategories.length > 0 && categoryUpdateMode !== 'remove' && (
                      <div className="space-y-2">
                        <Label>Default Category</Label>
                        <Select
                          value={defaultCategoryId?.toString() || ""}
                          onValueChange={(value) => setDefaultCategoryId(parseInt(value))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select default category" />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Show all categories in the dropdown, not just selected ones */}
                            {availableDefaultCategories.map(category => (
                              category && (
                                <SelectItem 
                                  key={category.id} 
                                  value={category.id.toString()}
                                >
                                  {category.name}
                                  {selectedCategories.includes(category.id) ? "" : " (existing)"}
                                </SelectItem>
                              )
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="text-xs text-muted-foreground">
                          The default category is used when not showing in all categories
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Plans Updates */}
              <div className="space-y-4 pb-4 border-b">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="updatePlans" 
                    checked={updatingPlans}
                    onCheckedChange={(checked) => setUpdatingPlans(!!checked)}
                  />
                  <Label htmlFor="updatePlans">Update Plans</Label>
                </div>
                
                {updatingPlans && (
                  <FormField
                    control={form.control}
                    name="plan_ids"
                    render={({ field }) => (
                      <FormItem className="pl-6">
                        <FormLabel>Select Plans</FormLabel>
                        <ScrollArea className="h-[120px] border rounded-md p-2">
                          <div className="space-y-2">
                            {plans?.map(plan => (
                              <div key={plan.id} className="flex items-center space-x-2">
                                <Checkbox 
                                  id={`plan-${plan.id}`} 
                                  checked={(field.value || []).includes(plan.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      field.onChange([...(field.value || []), plan.id]);
                                    } else {
                                      field.onChange((field.value || []).filter(id => id !== plan.id));
                                    }
                                  }}
                                />
                                <Label htmlFor={`plan-${plan.id}`} className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full"
                                    style={{
                                      backgroundColor: getPlanColor(plan.color_index).light,
                                      border: `1px solid ${getPlanColor(plan.color_index).dark}`
                                    }}
                                  />
                                  {plan.name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* Tag Updates */}
              {hasRegionContext && (
                <div className="space-y-4 pb-4 border-b">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="updateTags" 
                      checked={updatingTags}
                      onCheckedChange={(checked) => setUpdatingTags(!!checked)}
                    />
                    <Label htmlFor="updateTags">Update Tags</Label>
                  </div>
                  
                  {updatingTags && (
                    <div className="pl-6 space-y-4">
                      <RadioGroup
                        value={tagUpdateMode}
                        onValueChange={(value) => setTagUpdateMode(value as 'add' | 'replace' | 'remove')}
                        className="space-y-4"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="add" id="tag-option-add" />
                            <Label htmlFor="tag-option-add">Add tags</Label>
                          </div>
                          <p className="text-xs text-muted-foreground pl-6">
                            Add selected tags to channels (keeps existing tags)
                          </p>
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="replace" id="tag-option-replace" />
                            <Label htmlFor="tag-option-replace">Replace all tags</Label>
                          </div>
                          <p className="text-xs text-muted-foreground pl-6">
                            Replace all existing tags with selected tags
                          </p>
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="remove" id="tag-option-remove" />
                            <Label htmlFor="tag-option-remove">Remove tags</Label>
                          </div>
                          <p className="text-xs text-muted-foreground pl-6">
                            Remove selected tags from channels
                          </p>
                        </div>
                      </RadioGroup>

                      {tags && tags.length > 0 ? (
                        <div>
                          <Label className="text-sm font-medium">Select Tags</Label>
                          <ScrollArea className="h-[120px] border rounded-md p-2 mt-2">
                            <div className="space-y-2">
                              {tags.map(tag => (
                                <div key={tag.id} className="flex items-center space-x-2">
                                  <Checkbox 
                                    id={`tag-${tag.id}`} 
                                    checked={selectedTags.includes(tag.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setSelectedTags(prev => [...prev, tag.id]);
                                      } else {
                                        setSelectedTags(prev => prev.filter(id => id !== tag.id));
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`tag-${tag.id}`} className="flex items-center gap-2">
                                    {tag.name}
                                    <span className="text-xs text-muted-foreground">
                                      ({tag.channel_count} channels)
                                    </span>
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground pl-6">
                          No tags available. Create tags in the Tags tab first.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Description Update */}
              <div className="space-y-4 pb-4 border-b">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="updateDescription" 
                    checked={updatingDescription}
                    onCheckedChange={(checked) => setUpdatingDescription(!!checked)}
                  />
                  <Label htmlFor="updateDescription">Update Description</Label>
                </div>
                
                {updatingDescription && (
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="pl-6">
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Input placeholder="Channel description" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* Price Update */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="updatePrice" 
                    checked={updatingPrice}
                    onCheckedChange={(checked) => setUpdatingPrice(!!checked)}
                  />
                  <Label htmlFor="updatePrice">Update Price</Label>
                </div>
                
                {updatingPrice && (
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem className="pl-6">
                        <FormLabel>Price</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="0.00" 
                            {...field} 
                            value={field.value === null ? '' : field.value}
                            onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* Local Channel Status Update - only show when in a region context */}
              {hasRegionContext && (
                <div className="space-y-4 pb-4 border-b">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="updateLocalStatus" 
                      checked={updatingLocalStatus}
                      onCheckedChange={(checked) => setUpdatingLocalStatus(!!checked)}
                    />
                    <Label htmlFor="updateLocalStatus">Update Local Channel Status</Label>
                  </div>
                  
                  {updatingLocalStatus && (
                    <div className="pl-6 space-y-3">
                      <RadioGroup
                        value={isLocalChannel ? "local" : "non-local"}
                        onValueChange={(value) => setIsLocalChannel(value === "local")}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="local" id="local-channel" />
                          <Label htmlFor="local-channel">
                            Mark as local channel
                            <span className="block text-xs text-muted-foreground">
                              Local channels can have the same name across different regions
                            </span>
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2 mt-2">
                          <RadioGroupItem value="non-local" id="non-local-channel" />
                          <Label htmlFor="non-local-channel">
                            Mark as non-local channel
                            <span className="block text-xs text-muted-foreground">
                              Remove local status from channels
                            </span>
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleDialogClose}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={!(updatingCategory || updatingPlans || updatingDescription || updatingPrice || updatingLocalStatus || updatingTags) || bulkUpdateMutation.isPending}
                >
                  {bulkUpdateMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Channels"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
} 